import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import {
  FiSearch,
  FiUpload,
  FiLink,
  FiFile,
  FiFileText,
  FiImage,
  FiDollarSign,
  FiEdit3,
  FiTrash2,
  FiSave,
  FiX,
  FiLock,
  FiUnlock,
  FiCreditCard,
  FiArrowRight,
  FiArrowUpRight,
  FiMail,
  FiBookOpen,
  FiUser,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { db } from "./firebase";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import QRCode from "react-qr-code";
import Swal from "sweetalert2";
import Navbar from "./Navbar";
import Foot from "./Foot";
import "./Hero.css";  // design system: tokens, buttons, grain, .kk-progress
import "./Note.css";  // everything unique to this page

/* ═══════════════ DATA (presentational only — logic untouched) ═══════════════ */

const ADMIN_EMAIL = "karanksxxx@gmail.com";
const NOTE_TABS = ["All", "Java", "Web Dev", "DSA", "Others"];

const FALLBACK_IMG = {
  java: "/img/proj-realtycore.jpg",
  react: "/img/proj-products.jpg",
  tailwind: "/img/proj-listing.jpg",
  dsa: "/img/proj-notes.jpg",
  git: "/img/proj-aushlink.jpg",
  system: "/img/proj-darkhackers.jpg",
};

// Demo items for preview when Firebase is not configured.
// (In your project with real `db`, these are never used — onSnapshot loads Firestore.)
const DEMO_NOTES = [
  {
    id: "demo-1", title: "Java Basics",
    desc: "Arrays, strings, classes, objects, inheritance.",
    url: "data:text/plain;charset=utf-8," + encodeURIComponent("Java Basics — arrays, strings, classes, objects, inheritance."),
    uploadPic: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?q=80&w=600&auto=format&fit=crop",
    paymentAmount: null,
  },
  {
    id: "demo-2", title: "React Hooks",
    desc: "useState, useEffect, useContext and custom hooks.",
    url: "data:text/plain;charset=utf-8," + encodeURIComponent("React Hooks — useState, useEffect, useContext and custom hooks."),
    uploadPic: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?q=80&w=600&auto=format&fit=crop",
    paymentAmount: null,
  },
  {
    id: "demo-3", title: "Tailwind CSS",
    desc: "Utility-first styling and responsive design tips.",
    url: "data:text/plain;charset=utf-8," + encodeURIComponent("Tailwind CSS — utility-first styling and responsive design tips."),
    uploadPic: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=600&auto=format&fit=crop",
    paymentAmount: "49.00",
  },
  {
    id: "demo-4", title: "DSA Quick Revision",
    desc: "Arrays, LinkedList, Stack, Queue, Trees, Graphs.",
    url: "data:text/plain;charset=utf-8," + encodeURIComponent("DSA Quick Revision — arrays, linked list, stack, queue, trees, graphs."),
    uploadPic: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?q=80&w=600&auto=format&fit=crop",
    paymentAmount: null,
  },
  {
    id: "demo-5", title: "Git & GitHub",
    desc: "Commits and collaboration basics.",
    url: "data:text/plain;charset=utf-8," + encodeURIComponent("Git & GitHub — commits and collaboration basics."),
    uploadPic: "https://images.unsplash.com/photo-1556075798-4825dfaaf498?q=80&w=600&auto=format&fit=crop",
    paymentAmount: null,
  },
  {
    id: "demo-6", title: "System Design",
    desc: "Scalable architecture and best practices.",
    url: "data:text/plain;charset=utf-8," + encodeURIComponent("System Design — scalable architecture and best practices."),
    uploadPic: "https://images.unsplash.com/photo-1451187580459-43490279c429?q=80&w=600&auto=format&fit=crop",
    paymentAmount: "99.00",
  },
];

/* ═══════════════ ONE rAF LOOP — [data-plx] ═══════════════ */

function useParallax(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const items = Array.from(root.querySelectorAll("[data-plx]"));
    let raf = 0;

    const render = () => {
      const vh = window.innerHeight;
      for (const el of items) {
        const speed = parseFloat(el.dataset.plx) || 0;
        const r = el.getBoundingClientRect();
        if (r.bottom < -420 || r.top > vh + 420) continue;
        const y = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
      }
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => cancelAnimationFrame(raf);
  }, [rootRef]);
}

// ─── Floating Particles ────────────────────────────────────────
function NoteParticles() {
  const parts = useRef(
    Array.from({ length: 20 }, (_, i) => ({
      id: i, x: Math.random() * 100, y: Math.random() * 100,
      s: Math.random() * 3 + 1, d: Math.random() * 12 + 8, dl: Math.random() * 5,
    }))
  );
  return (
    <div className="nt-particles" aria-hidden="true">
      {parts.current.map((p) => (
        <motion.span
          key={p.id}
          className="nt-particle"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.s, height: p.s }}
          animate={{ y: [0, -20, 0], opacity: [0.08, 0.4, 0.08] }}
          transition={{ duration: p.d, repeat: Infinity, delay: p.dl, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

// ─── Note Card ─────────────────────────────────────────────────
function NoteCard({
  note, index, user, isAdmin, editModeId,
  editTitle, editLink, editPic, editPrice,
  onEdit, onSaveEdit, onCancelEdit,
  onDelete, onPay, onEditChange
}) {
  const isEditing = editModeId === note.id;
  const isLoggedIn = !!user;
  const hasPrice = note.paymentAmount && parseFloat(note.paymentAmount) > 0;

  const fallbackImage = "https://cdn.jsdelivr.net/gh/koehlersimon/fallback-patterns/abstract1.png";
  const localFallback =
    Object.entries(FALLBACK_IMG).find(([k]) => note.title.toLowerCase().includes(k))?.[1] ||
    "/img/proj-notes.jpg";

  return (
    <motion.div
      className={`nt-card ${isEditing ? "is-editing" : ""}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: (index % 3) * 0.08, duration: 0.5 }}
      layout
    >
      {/* Cover */}
      <div className="nt-card__cover">
        <img
          src={note.uploadPic || note.img || fallbackImage}
          alt=""
          loading="lazy"
          onError={(e) => {
            if (!e.currentTarget.dataset.fb) {
              e.currentTarget.dataset.fb = "1";
              e.currentTarget.src = localFallback;
            }
          }}
        />
        <span className="nt-card__shade" aria-hidden="true" />

        <span className="nt-card__badge nt-card__badge--type">
          <FiBookOpen /> Notes
        </span>

        {hasPrice && (
          <span className="nt-card__badge nt-card__badge--price">
            ₹{parseFloat(note.paymentAmount).toFixed(2)}
          </span>
        )}

        <span className="nt-card__no">{String(index + 1).padStart(2, "0")}</span>
      </div>

      {/* Body */}
      <div className="nt-card__body">
        {isEditing ? (
          <div className="nt-edit">
            <label className="nt-field">
              <span>Title</span>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => onEditChange("title", e.target.value)}
                placeholder="Note title"
              />
            </label>
            <label className="nt-field">
              <span>URL</span>
              <input
                type="text"
                value={editLink}
                onChange={(e) => onEditChange("link", e.target.value)}
                placeholder="File URL"
              />
            </label>
            <label className="nt-field">
              <span>Preview image</span>
              <input
                type="text"
                value={editPic}
                onChange={(e) => onEditChange("pic", e.target.value)}
                placeholder="Image URL"
              />
            </label>
            <label className="nt-field">
              <span>Price (₹)</span>
              <input
                type="number"
                value={editPrice}
                onChange={(e) => onEditChange("price", e.target.value)}
                placeholder="0.00"
              />
            </label>
            <div className="nt-edit__btns">
              <motion.button
                className="kk-btn"
                onClick={() => onSaveEdit(note.id)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <FiSave /> Save
              </motion.button>
              <motion.button
                className="kk-btn kk-btn--ghost"
                onClick={onCancelEdit}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <FiX /> Cancel
              </motion.button>
            </div>
          </div>
        ) : (
          <>
            <h3 className="nt-card__title">{note.title}</h3>
            {note.desc && <p className="nt-card__desc">{note.desc}</p>}

            <div className="nt-card__actions">
              {isLoggedIn ? (
                <motion.a
                  href={note.link || note.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="kk-btn nt-btn-open"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <FiUnlock /> Open
                </motion.a>
              ) : (
                <button className="kk-btn kk-btn--ghost nt-btn-open is-locked" disabled>
                  <FiLock /> Login required
                </button>
              )}

              {!isAdmin && hasPrice && (
                <motion.button
                  className="kk-btn nt-btn-pay"
                  onClick={() => onPay(note)}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <FiCreditCard /> ₹{parseFloat(note.paymentAmount).toFixed(2)}
                </motion.button>
              )}

              {isAdmin && !isEditing && (
                <div className="nt-card__admin">
                  <motion.button
                    className="nt-icon-btn"
                    onClick={() => onEdit(note)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title="Edit"
                  >
                    <FiEdit3 />
                  </motion.button>
                  <motion.button
                    className="nt-icon-btn is-danger"
                    onClick={() => onDelete(note.id)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title="Delete"
                  >
                    <FiTrash2 />
                  </motion.button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}

// ─── Admin Upload Form ─────────────────────────────────────────
function AdminUploadForm({
  uploadMethod, setUploadMethod,
  fileTitle, setFileTitle,
  fileLink, setFileLink,
  uploadPic, setUploadPic,
  paymentAmount, setPaymentAmount,
  filePreview, onFileChange,
  onUploadFromLink, onUploadFromFile
}) {
  return (
    <motion.section
      className="nt-upload"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <div className="nt-upload__head">
        <span className="kk-eyebrow">Admin — Library</span>
        <h2 className="kk-h2">
          Upload a new <span className="kk-serif kk-acid">note</span>
        </h2>
      </div>

      <div className="nt-method">
        <button
          className={`nt-method__btn ${uploadMethod === "file" ? "is-active" : ""}`}
          onClick={() => setUploadMethod("file")}
          type="button"
        >
          <FiFile /> Upload file
        </button>
        <button
          className={`nt-method__btn ${uploadMethod === "link" ? "is-active" : ""}`}
          onClick={() => setUploadMethod("link")}
          type="button"
        >
          <FiLink /> Upload by URL
        </button>
      </div>

      <div className="nt-form">
        <div className="nt-form__row">
          <label className="nt-field" style={{ flex: 1 }}>
            <span>
              <FiFileText /> Title
            </span>
            <input
              type="text"
              value={fileTitle}
              onChange={(e) => setFileTitle(e.target.value)}
              placeholder="Note title"
            />
          </label>
          <label className="nt-field" style={{ flex: 1 }}>
            <span>
              <FiDollarSign /> Price (₹) — optional
            </span>
            <input
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              placeholder="0.00"
            />
          </label>
        </div>

        {uploadMethod === "file" ? (
          <>
            <div className="nt-field">
              <span>
                <FiFile /> File
              </span>
              <div className="nt-file-row">
                <input type="file" onChange={onFileChange} id="note-file" className="nt-file-input" />
                <label htmlFor="note-file" className="kk-btn kk-btn--ghost">
                  <FiFile /> Choose file
                </label>
                {filePreview && (
                  <span className="nt-file-status">
                    <FiImage /> File selected
                  </span>
                )}
              </div>
            </div>
            <label className="nt-field">
              <span>
                <FiImage /> Preview image URL
              </span>
              <input
                type="text"
                value={uploadPic}
                onChange={(e) => setUploadPic(e.target.value)}
                placeholder="https://..."
              />
            </label>
            <motion.button
              className="kk-btn nt-submit"
              onClick={onUploadFromFile}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiUpload /> Upload file
            </motion.button>
          </>
        ) : (
          <>
            <label className="nt-field">
              <span>
                <FiLink /> File URL
              </span>
              <input
                type="text"
                value={fileLink}
                onChange={(e) => setFileLink(e.target.value)}
                placeholder="https://..."
              />
            </label>
            <label className="nt-field">
              <span>
                <FiImage /> Preview image URL
              </span>
              <input
                type="text"
                value={uploadPic}
                onChange={(e) => setUploadPic(e.target.value)}
                placeholder="https://..."
              />
            </label>
            <motion.button
              className="kk-btn nt-submit"
              onClick={onUploadFromLink}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiLink /> Upload URL
            </motion.button>
          </>
        )}
      </div>
    </motion.section>
  );
}

// ─── QR Payment Modal ──────────────────────────────────────────
function QRPaymentModal({ note, onClose }) {
  const amount = parseFloat(note.paymentAmount);
  const upiLink = `upi://pay?pa=karanksxxx-1@oksbi&pn=Karan%20Kumar&am=${amount.toFixed(2)}&cu=INR`;

  return (
    <motion.div
      className="nt-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="nt-qr"
        initial={{ scale: 0.92, y: 30, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.92, y: 30, opacity: 0 }}
        transition={{ type: "spring", damping: 26, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="nt-qr__head">
          <div>
            <span className="kk-eyebrow">UPI payment</span>
            <h2>Scan &amp; pay ₹{amount.toFixed(2)}</h2>
          </div>
          <motion.button
            className="nt-qr__x"
            onClick={onClose}
            whileHover={{ rotate: 90 }}
            aria-label="Close"
          >
            <FiX />
          </motion.button>
        </div>

        <div className="nt-qr__body">
          <div className="nt-qr__box">
            <QRCode value={upiLink} size={190} bgColor="#0a0a0c" fgColor="#e9efff" level="H" />
            <span className="nt-qr__corner tl" />
            <span className="nt-qr__corner tr" />
            <span className="nt-qr__corner bl" />
            <span className="nt-qr__corner br" />
          </div>

          <p className="nt-qr__amt">₹{amount.toFixed(2)}</p>
          <p className="nt-qr__merchant">Karan Kumar</p>
          <p className="nt-qr__upi">karanksxxx-1@oksbi</p>
          <p className="nt-qr__note">Paying for: {note.title}</p>

          <a className="kk-btn kk-btn--ghost nt-qr__upi-btn" href={upiLink}>
            <FiArrowUpRight /> Open UPI app
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Empty State ───────────────────────────────────────────────
function EmptyState({ isLoggedIn }) {
  return (
    <motion.div className="nt-empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="nt-empty__ic">
        <FiFileText />
      </div>
      <h3>{isLoggedIn ? "No notes found" : "Login required"}</h3>
      <p>
        {isLoggedIn
          ? "No notes match your search. Try different keywords."
          : "Please login to access the notes section."}
      </p>
    </motion.div>
  );
}

// ─── Main Note Component ───────────────────────────────────────
// LOGIC: identical to your original — same state, same handlers, same
// Firebase calls. Only styling/classes + parallax + preview fallback.
export default function Note() {
  const rootRef = useRef(null);
  const [activeTab, setActiveTab] = useState("All");
  const [user, setUser] = useState(null);
  const [fileLink, setFileLink] = useState("");
  const [fileTitle, setFileTitle] = useState("");
  const [uploads, setUploads] = useState([]);
  const [filePreview, setFilePreview] = useState(null);
  const [uploadMethod, setUploadMethod] = useState("file");
  const [uploadPic, setUploadPic] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [showPaymentQR, setShowPaymentQR] = useState(false);
  const [currentPaymentNote, setCurrentPaymentNote] = useState(null);
  const [editModeId, setEditModeId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editLink, setEditLink] = useState("");
  const [editPic, setEditPic] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [loading, setLoading] = useState(true);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28 });

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // Preview-safe refs: identical to collection(db, ...) when db exists.
  const notesRef = useMemo(() => { try { return db ? collection(db, "Notes") : null; } catch { return null; } }, []);
  const searchRef = useMemo(() => { try { return db ? collection(db, "Searches") : null; } catch { return null; } }, []);
  const isAdmin = user?.Email === ADMIN_EMAIL;

  useParallax(rootRef);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (userData) setUser(userData);

    if (!notesRef) {
      const t = setTimeout(() => { setUploads(DEMO_NOTES); setLoading(false); }, 600);
      return () => clearTimeout(t);
    }
    const unsubscribe = onSnapshot(notesRef, (snapshot) => {
      const fetched = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUploads(fetched);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [notesRef]);

  const filteredNotes = uploads.filter((note) => {
    const matchSearch = note.title.toLowerCase().includes(searchQuery.toLowerCase());
    if (activeTab === "All") return matchSearch;
    if (activeTab === "Others") {
      const known = ["java", "web dev", "react", "tailwind", "css", "html", "javascript", "js", "dsa"];
      const inTitle = known.some((k) => note.title.toLowerCase().includes(k));
      return matchSearch && !inTitle;
    }
    if (activeTab === "Web Dev") {
      const keys = ["web dev", "react", "tailwind", "css", "html", "javascript", "js"];
      return matchSearch && keys.some((k) => note.title.toLowerCase().includes(k));
    }
    const t = activeTab.toLowerCase();
    return matchSearch && note.title.toLowerCase().includes(t);
  });

  const generateUPIURL = (amount) => {
    const upiID = "karanksxxx-1@oksbi";
    const storeName = "Karan Kumar";
    if (isNaN(amount) || amount <= 0) return "";
    return `upi://pay?pa=${upiID}&pn=${encodeURIComponent(storeName)}&am=${amount.toFixed(2)}&cu=INR`;
  };

  const handlePayNow = (note) => {
    if (!note.paymentAmount || parseFloat(note.paymentAmount) <= 0) {
      Swal.fire({
        title: "Invalid Amount",
        text: "This note does not have a valid payment amount set.",
        icon: "warning",
        background: "#0a0a0c",
        color: "#f3f3f5",
      });
      return;
    }

    setCurrentPaymentNote(note);

    if (isMobile) {
      localStorage.setItem("paymentInProgress", "true");
      window.location.href = generateUPIURL(parseFloat(note.paymentAmount));
    } else {
      setShowPaymentQR(true);
    }
  };

  const handleFileUploadFromLink = async () => {
    if (!isAdmin) {
      Swal.fire({ title: "Access Denied", text: "Only admin can upload.", icon: "error", background: "#0a0a0c", color: "#f3f3f5" });
      return;
    }
    if (!fileLink || !fileTitle) {
      Swal.fire({ title: "Missing Fields", text: "Provide both file URL and title.", icon: "warning", background: "#0a0a0c", color: "#f3f3f5" });
      return;
    }
    if (!notesRef) {
      setUploads((p) => [{ id: "local-" + Date.now(), title: fileTitle, url: fileLink, uploadPic: uploadPic || "", paymentAmount: paymentAmount ? parseFloat(paymentAmount).toFixed(2) : null }, ...p]);
      Swal.fire({ title: "Uploaded!", text: "Note added successfully.", icon: "success", background: "#0a0a0c", color: "#f3f3f5" });
      setFileLink(""); setFileTitle(""); setUploadPic(""); setPaymentAmount("");
      return;
    }
    await addDoc(notesRef, {
      title: fileTitle,
      url: fileLink,
      uploadPic: uploadPic || "",
      paymentAmount: paymentAmount ? parseFloat(paymentAmount).toFixed(2) : null,
    });
    Swal.fire({ title: "Uploaded!", text: "Note added successfully.", icon: "success", background: "#0a0a0c", color: "#f3f3f5" });
    setFileLink(""); setFileTitle(""); setUploadPic(""); setPaymentAmount("");
  };

  const handleFileUploadFromFile = async () => {
    if (!isAdmin) {
      Swal.fire({ title: "Access Denied", text: "Only admin can upload.", icon: "error", background: "#0a0a0c", color: "#f3f3f5" });
      return;
    }
    if (!filePreview || !fileTitle) {
      Swal.fire({ title: "Missing Fields", text: "Select a file and enter a title.", icon: "warning", background: "#0a0a0c", color: "#f3f3f5" });
      return;
    }
    if (!notesRef) {
      setUploads((p) => [{ id: "local-" + Date.now(), title: fileTitle, url: filePreview, uploadPic: uploadPic || "", paymentAmount: paymentAmount ? parseFloat(paymentAmount).toFixed(2) : null }, ...p]);
      Swal.fire({ title: "Uploaded!", text: "Note added successfully.", icon: "success", background: "#0a0a0c", color: "#f3f3f5" });
      setFileTitle(""); setFilePreview(null); setUploadPic(""); setPaymentAmount("");
      return;
    }
    await addDoc(notesRef, {
      title: fileTitle,
      url: filePreview,
      uploadPic: uploadPic || "",
      paymentAmount: paymentAmount ? parseFloat(paymentAmount).toFixed(2) : null,
    });
    Swal.fire({ title: "Uploaded!", text: "Note added successfully.", icon: "success", background: "#0a0a0c", color: "#f3f3f5" });
    setFileTitle(""); setFilePreview(null); setUploadPic(""); setPaymentAmount("");
  };

  const deleteUpload = async (id) => {
    if (!isAdmin) return;
    const result = await Swal.fire({
      title: "Delete Note?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f43f5e",
      cancelButtonColor: "#3b4a6b",
      confirmButtonText: "Delete",
      background: "#0a0a0c",
      color: "#f3f3f5",
    });
    if (result.isConfirmed) {
      if (!db || String(id).startsWith("demo-") || String(id).startsWith("local-")) {
        setUploads((p) => p.filter((u) => u.id !== id));
        Swal.fire({ title: "Deleted!", text: "Note removed.", icon: "success", background: "#0a0a0c", color: "#f3f3f5" });
        return;
      }
      await deleteDoc(doc(db, "Notes", id));
      Swal.fire({ title: "Deleted!", text: "Note removed.", icon: "success", background: "#0a0a0c", color: "#f3f3f5" });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileTitle(file.name);
      setFilePreview(URL.createObjectURL(file));
    }
  };

  const handleEdit = (note) => {
    setEditModeId(note.id);
    setEditTitle(note.title);
    setEditLink(note.url);
    setEditPic(note.uploadPic || "");
    setEditPrice(note.paymentAmount ? parseFloat(note.paymentAmount) : "");
  };

  const handleEditChange = (field, value) => {
    switch (field) {
      case "title": setEditTitle(value); break;
      case "link": setEditLink(value); break;
      case "pic": setEditPic(value); break;
      case "price": setEditPrice(value); break;
      default: break;
    }
  };

  const handleSaveEdit = async (id) => {
    if (!db || String(id).startsWith("demo-") || String(id).startsWith("local-")) {
      setUploads((p) => p.map((u) => u.id === id
        ? { ...u, title: editTitle, url: editLink, uploadPic: editPic, paymentAmount: editPrice ? parseFloat(editPrice).toFixed(2) : null }
        : u));
      setEditModeId(null);
      Swal.fire({ title: "Updated!", text: "Note updated successfully.", icon: "success", background: "#0a0a0c", color: "#f3f3f5" });
      return;
    }
    await updateDoc(doc(db, "Notes", id), {
      title: editTitle,
      url: editLink,
      uploadPic: editPic,
      paymentAmount: editPrice ? parseFloat(editPrice).toFixed(2) : null,
    });
    setEditModeId(null);
    Swal.fire({ title: "Updated!", text: "Note updated successfully.", icon: "success", background: "#0a0a0c", color: "#f3f3f5" });
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!searchRef) return;
    if (query.trim() !== "" && user) {
      try {
        await addDoc(searchRef, {
          query: query.trim(),
          user: user.Email || "Guest",
          timestamp: serverTimestamp(),
        });
      } catch (error) {
        console.error("Error saving search:", error);
      }
    }
  };

  return (
    <div className="kk-root nt-page" ref={rootRef}>
      <motion.div className="kk-progress" style={{ scaleX: progress }} />
      <NoteParticles />

    

      {/* ══════════════ HERO ══════════════ */}
      <header className="nt-hero">
        <div className="nt-hero__bg" aria-hidden="true">
          <span className="nt-hero__grid" data-plx="0.05" />
          <span className="nt-hero__glow nt-hero__glow--acid" data-plx="0.16" />
          <span className="nt-hero__glow nt-hero__glow--violet" data-plx="0.24" />
          <span className="nt-hero__word" data-plx="0.1">
            LIBRARY
          </span>
        </div>

        <div className="kk-wrap nt-hero__in">
          <motion.div
            className="nt-hero__copy"
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="nt-kicker">
              <span className="kk-dot" />
              <span>Notes</span>
              <span className="kk-slash">/</span>
              <span>Resources</span>
            </div>

            <h1 className="nt-title">
              <motion.span
                className="nt-title__line"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              >
                My <span className="kk-serif kk-acid">Notes</span>
              </motion.span>
              <motion.span
                className="nt-title__sub"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                decode, revise, build.
              </motion.span>
            </h1>

            <p className="nt-lead">
              Decode notes, concepts, projects and important resources — free to read,
              some premium, all written by hand.
            </p>

            {user && (
              <motion.div
                className="nt-greet"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <FiUser /> Welcome, <strong>{user.Name}</strong>
              </motion.div>
            )}

            <div className="nt-hero__meta">
              <div>
                <b>{uploads.length || 6}</b>
                <span>Notes</span>
              </div>
              <div>
                <b>05</b>
                <span>Topics</span>
              </div>
              <div>
                <b>₹49</b>
                <span>From</span>
              </div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* ══════════════ TOOLBAR ══════════════ */}
      <div className="nt-bar">
        <div className="kk-wrap nt-bar__in">
          <div className="nt-tabs">
            {NOTE_TABS.map((t) => (
              <button
                key={t}
                className={`nt-tab ${activeTab === t ? "is-active" : ""}`}
                onClick={() => setActiveTab(t)}
                type="button"
              >
                {t}
                {t === "All" && <em>{filteredNotes.length}</em>}
              </button>
            ))}
          </div>

          <div className="nt-search">
            <FiSearch className="nt-search__ic" />
            <input
              type="text"
              placeholder="Search notes..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
            {searchQuery && (
              <motion.button
                className="nt-search__x"
                onClick={() => setSearchQuery("")}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                type="button"
                aria-label="Clear search"
              >
                <FiX />
              </motion.button>
            )}
          </div>

          <span className="nt-count">
            <FiFileText /> {filteredNotes.length} notes
          </span>
        </div>
      </div>

      {/* ══════════════ MAIN ══════════════ */}
      <main className="kk-section kk-tight nt-main">
        <div className="kk-wrap">
          {/* Admin upload */}
          {isAdmin && (
            <AdminUploadForm
              uploadMethod={uploadMethod}
              setUploadMethod={setUploadMethod}
              fileTitle={fileTitle}
              setFileTitle={setFileTitle}
              fileLink={fileLink}
              setFileLink={setFileLink}
              uploadPic={uploadPic}
              setUploadPic={setUploadPic}
              paymentAmount={paymentAmount}
              setPaymentAmount={setPaymentAmount}
              filePreview={filePreview}
              onFileChange={handleFileChange}
              onUploadFromLink={handleFileUploadFromLink}
              onUploadFromFile={handleFileUploadFromFile}
            />
          )}

          {/* Login warning */}
          {!user && (
            <motion.div className="nt-warn" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <span className="nt-warn__ic">
                <FiLock />
              </span>
              <div>
                <b>Login required</b>
                <p>You must log in to open the notes. Browsing is free.</p>
              </div>
              <a className="kk-btn kk-btn--ghost" href="/login">
                Log in <FiArrowRight />
              </a>
            </motion.div>
          )}

          {/* Section head */}
          <div className="nt-sec-head">
            <div>
              <span className="kk-eyebrow kk-eyebrow--violet">02 — Library</span>
              <h2 className="kk-h2">
                Available
                <br />
                <span className="kk-serif">notes</span>
              </h2>
            </div>
            <div className="nt-sec-head__aside">
              <p>
                Every note is a single file — open it in a new tab, keep it, revise from it.
                Paid notes unlock through UPI.
              </p>
            </div>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="nt-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <motion.div
                  key={i}
                  className="nt-skel"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.08 }}
                >
                  <div className="nt-skel__img" />
                  <div className="nt-skel__body">
                    <div className="nt-skel__line" />
                    <div className="nt-skel__btn" />
                  </div>
                </motion.div>
              ))}
            </div>
          ) : filteredNotes.length === 0 ? (
            <EmptyState isLoggedIn={!!user} />
          ) : (
            <div className="nt-grid">
              {filteredNotes.map((note, index) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  index={index}
                  user={user}
                  isAdmin={isAdmin}
                  editModeId={editModeId}
                  editTitle={editTitle}
                  editLink={editLink}
                  editPic={editPic}
                  editPrice={editPrice}
                  onEdit={handleEdit}
                  onSaveEdit={handleSaveEdit}
                  onCancelEdit={() => setEditModeId(null)}
                  onDelete={deleteUpload}
                  onPay={handlePayNow}
                  onEditChange={handleEditChange}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* ══════════════ CTA ══════════════ */}
      <section className="nt-cta">
        <div className="nt-cta__orbs" aria-hidden="true">
          <span data-plx="0.1" />
          <span data-plx="0.18" />
        </div>
        <div className="kk-wrap nt-cta__in">
          <span className="kk-eyebrow">Missing something?</span>
          <h2 className="kk-h2">
            Want a note on{" "}
            <span className="kk-serif kk-acid">your topic?</span>
          </h2>
          <p>Tell me the subject and I will add it to the library — free for students.</p>
          <div className="nt-cta__btns">
            <a className="kk-btn kk-btn--lg" href="mailto:karanksxxx@gmail.com?subject=Note%20request">
              <FiMail /> Request a note
            </a>
            <a className="kk-btn kk-btn--ghost kk-btn--lg" href="/blogs">
              Read the blog <FiArrowUpRight />
            </a>
          </div>
        </div>
      </section>

      <Foot />

      <a
        className="kk-wa"
        href="https://wa.me/919041429065"
        target="_blank"
        rel="noreferrer"
        title="Chat on WhatsApp"
      >
        <FaWhatsapp />
      </a>
  <Navbar />
      {/* ═══════════ QR MODAL ═══════════ */}
      <AnimatePresence>
        {showPaymentQR && currentPaymentNote && (
          <QRPaymentModal note={currentPaymentNote} onClose={() => setShowPaymentQR(false)} />
        )}
      </AnimatePresence>
    </div>
  );
}
